/**
 * The program page document — a theme plus an ordered list of blocks.
 *
 * Mirrors src/program-page/program-page.document.ts in the backend, which is
 * the authority: it rebuilds every document field by field on save, so
 * anything the editor sends beyond these shapes is dropped or clamped there.
 */

export const PROGRAM_PAGE_LIMITS = {
  blocks: 24,
  items: 12,
  tierItems: 8,
  listLines: 12,
} as const;

export type ProgramPageIcon =
  | { kind: "lucide"; value: string }
  | { kind: "emoji"; value: string }
  | { kind: "image"; value: string };

export interface ProgramPageCta {
  label: string;
  /** `join` resolves to the brand's join page; `url` opens `url`. */
  action: "join" | "url";
  url: string | null;
}

/**
 * A colour is null to inherit the app's own theme — Inter, the semantic
 * surface tokens and the portal's brand accent, in light or dark — so the
 * page reads as part of Reveshare. A brand may override any of the four.
 */
export interface ProgramPageColors {
  background: string | null;
  surface: string | null;
  text: string | null;
  accent: string | null;
}

export interface ProgramPageTheme {
  colors: ProgramPageColors;
  /** "01 / 02 / 03" section counters above headings. */
  sectionNumbers: boolean;
}

export interface ProgramPageTierItem {
  id: string;
  /** The ProgramTier this card presents, or null for a display-only rung. */
  tierId: string | null;
  name: string;
  badge: string;
  tagline: string;
  /** Overrides the commission shown; empty means "use the linked tier's". */
  commissionLabel: string;
  icon: ProgramPageIcon | null;
  color: string | null;
  imageUrl: string | null;
  requirementsTitle: string;
  requirements: string[];
  rewardsTitle: string;
  rewards: string[];
}

export interface HeroBlock {
  id: string;
  type: "hero";
  layout: "centered" | "split" | "cover";
  eyebrow: string;
  title: string;
  subtitle: string;
  imageUrl: string | null;
  cta: ProgramPageCta | null;
}

export interface TiersBlock {
  id: string;
  type: "tiers";
  heading: string;
  subheading: string;
  /** Null means the brand's default program. */
  programId: string | null;
  layout: "cards" | "ladder" | "table";
  showProgress: boolean;
  showThresholds: boolean;
  items: ProgramPageTierItem[];
}

export interface FeaturesBlock {
  id: string;
  type: "features";
  heading: string;
  subheading: string;
  columns: 2 | 3 | 4;
  items: { id: string; icon: ProgramPageIcon | null; title: string; text: string }[];
}

export interface StepsBlock {
  id: string;
  type: "steps";
  heading: string;
  subheading: string;
  items: { id: string; title: string; text: string }[];
}

export interface StatsBlock {
  id: string;
  type: "stats";
  items: { id: string; value: string; label: string }[];
}

export interface TextBlock {
  id: string;
  type: "text";
  heading: string;
  body: string;
  align: "left" | "center";
}

export interface ImageBlock {
  id: string;
  type: "image";
  imageUrl: string | null;
  alt: string;
  caption: string;
}

export interface FaqBlock {
  id: string;
  type: "faq";
  heading: string;
  items: { id: string; question: string; answer: string }[];
}

export interface CtaBlock {
  id: string;
  type: "cta";
  title: string;
  subtitle: string;
  cta: ProgramPageCta | null;
}

export type ProgramPageBlock =
  | HeroBlock
  | TiersBlock
  | FeaturesBlock
  | StepsBlock
  | StatsBlock
  | TextBlock
  | ImageBlock
  | FaqBlock
  | CtaBlock;

export type ProgramPageBlockType = ProgramPageBlock["type"];

export interface ProgramPageDocument {
  version: number;
  theme: ProgramPageTheme;
  blocks: ProgramPageBlock[];
}

/** A live rung, straight from ProgramTier. */
export interface ProgramPageTier {
  id: string;
  name: string;
  threshold: number;
  commissionValue: number;
  perks: string | null;
}

export interface ProgramPageProgram {
  id: string;
  name: string | null;
  isDefault: boolean;
  commissionType: "PERCENTAGE" | "FIXED";
  commissionValue: number;
  tiers: ProgramPageTier[];
}

/** Where the signed-in affiliate stands on one program's ladder. */
export interface ProgramPageStanding {
  totalSales: number;
  totalOrders: number;
  currentTierId: string | null;
  nextTierId: string | null;
  progress: number;
  remainingToNext: number;
}
